import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  type KnowledgeDocumentDetail,
  useDiscardDraft,
  useKnowledgeDocument,
  usePublishVersion,
  useSaveDraft,
  useUpdateKnowledgeDocument,
} from '../../../../../../models/knowledgeDocument'

const AUTOSAVE_MS = 1500

type Props = {
  slug: string
  onOpenHistory: () => void
}

export function DocumentEditor({ slug, onOpenHistory }: Props) {
  const { data: doc, isLoading } = useKnowledgeDocument(slug)

  if (isLoading || !doc) {
    return <Skeleton className="h-96 w-full" />
  }

  // Key by slug + current version id so the editor fully remounts with
  // fresh initial content when the user switches documents or when the
  // published version changes (publish/revert). MDXEditor is uncontrolled
  // internally and doesn't re-sync from the `value` prop after mount.
  const editorKey = `${slug}:${doc.currentVersion?.id ?? 'empty'}`
  return <LoadedEditor key={editorKey} doc={doc} slug={slug} onOpenHistory={onOpenHistory} />
}

function LoadedEditor({
  doc,
  slug,
  onOpenHistory,
}: {
  doc: KnowledgeDocumentDetail
  slug: string
  onOpenHistory: () => void
}) {
  const publishedContent = doc.currentVersion?.content ?? ''
  const serverDraft = doc.draftContent
  const initialContent = serverDraft || publishedContent

  const [content, setContent] = useState(initialContent)
  const [editorInstance, setEditorInstance] = useState(0)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(doc.title)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const saveDraftMutation = useSaveDraft()
  const discardDraftMutation = useDiscardDraft()
  const publishMutation = usePublishVersion()
  const updateDocMutation = useUpdateKnowledgeDocument()

  const contentRef = useRef(content)
  const lastSavedRef = useRef(serverDraft)
  contentRef.current = content

  useEffect(() => {
    if (content === lastSavedRef.current) return
    const handle = setTimeout(() => {
      saveDraftMutation.mutate(
        { slug, draftContent: content },
        {
          onSuccess: (res) => {
            lastSavedRef.current = content
            setSavedAt(new Date(res.draftUpdatedAt))
          },
        }
      )
    }, AUTOSAVE_MS)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, slug])

  useEffect(() => {
    return () => {
      if (contentRef.current !== lastSavedRef.current) {
        saveDraftMutation.mutate({ slug, draftContent: contentRef.current })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const hasUnpublishedChanges = content !== publishedContent

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {isEditingTitle ? (
            <Input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => {
                const trimmed = titleDraft.trim()
                if (trimmed && trimmed !== doc.title) {
                  updateDocMutation.mutate({ slug, title: trimmed })
                }
                setIsEditingTitle(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                if (e.key === 'Escape') {
                  setTitleDraft(doc.title)
                  setIsEditingTitle(false)
                }
              }}
              className="text-xl font-semibold"
            />
          ) : (
            <button
              onClick={() => setIsEditingTitle(true)}
              className="truncate text-xl font-semibold hover:underline"
            >
              {doc.title}
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={doc.isEnabled}
              onCheckedChange={(checked) => updateDocMutation.mutate({ slug, isEnabled: checked })}
            />
            {doc.isEnabled ? 'Enabled' : 'Disabled'}
          </label>
          <label
            className="flex items-center gap-2 text-sm"
            title="Private docs are exposed only to the internal staff agent, never to customer-facing agents."
          >
            <Switch
              checked={doc.isPrivate}
              onCheckedChange={(checked) => updateDocMutation.mutate({ slug, isPrivate: checked })}
            />
            Private
          </label>
          <Button variant="outline" size="sm" onClick={onOpenHistory}>
            History
          </Button>
          {hasUnpublishedChanges && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                discardDraftMutation.mutate(slug, {
                  onSuccess: () => {
                    setContent(publishedContent)
                    lastSavedRef.current = ''
                    setEditorInstance((i) => i + 1)
                  },
                })
              }}
            >
              Discard changes
            </Button>
          )}
          <Button
            size="sm"
            onClick={async () => {
              if (content !== lastSavedRef.current) {
                const res = await saveDraftMutation.mutateAsync({ slug, draftContent: content })
                lastSavedRef.current = content
                setSavedAt(new Date(res.draftUpdatedAt))
              }
              publishMutation.mutate(slug)
            }}
            disabled={!hasUnpublishedChanges || publishMutation.isPending || saveDraftMutation.isPending}
            loading={publishMutation.isPending ? 'Publishing…' : undefined}
          >
            Publish
          </Button>
        </div>
      </div>

      {doc.currentVersion && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          Current: v{doc.currentVersion.versionNumber} ·{' '}
          {format(new Date(doc.currentVersion.dateCreated), 'PPpp')} ·{' '}
          {doc.currentVersion.createdByEmail ?? '—'}
          {doc.currentVersion.changeNote && (
            <Badge variant="outline" className="ml-2 text-xs">
              {doc.currentVersion.changeNote}
            </Badge>
          )}
          <div className="ml-auto">
            <DraftIndicator isSaving={saveDraftMutation.isPending} savedAt={savedAt} />
          </div>
        </div>
      )}

      <MarkdownEditor
        key={editorInstance}
        value={content}
        onChange={setContent}
        className="flex-1 border-none"
        contentEditableClassName="border-none"
      />
    </div>
  )
}

function DraftIndicator({ isSaving, savedAt }: { isSaving: boolean; savedAt: Date | null }) {
  if (isSaving) {
    return <span className="text-xs text-gray-500">Saving…</span>
  }
  if (savedAt) {
    return <span className="text-xs text-gray-500">Saved at {format(savedAt, 'p')}</span>
  }
  return null
}
